import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { Copy, Eye, Code, Plus, GripVertical, Settings, CheckCircle, ToggleLeft, ToggleRight, Edit2, ChevronDown, MessageCircle, Phone, Smartphone, Palette } from 'lucide-react';
import { toast } from 'sonner';

// Códigos de país más comunes
const COUNTRY_CODES = [
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+1', country: 'USA/Canadá', flag: '🇺🇸' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦' },
  { code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻' },
  { code: '+504', country: 'Honduras', flag: '🇭🇳' },
  { code: '+505', country: 'Nicaragua', flag: '🇳🇮' },
  { code: '+591', country: 'Bolivia', flag: '🇧🇴' },
  { code: '+595', country: 'Paraguay', flag: '🇵🇾' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
  { code: '+44', country: 'Reino Unido', flag: '🇬🇧' },
  { code: '+33', country: 'Francia', flag: '🇫🇷' },
  { code: '+49', country: 'Alemania', flag: '🇩🇪' },
  { code: '+39', country: 'Italia', flag: '🇮🇹' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
];

// Campos base con opción de visibilidad en formularios públicos
const DEFAULT_BASE_FIELDS = [
  { id: 'base_name', name: 'name', label: 'Nombre Contacto', type: 'text', required: true, isBase: true, showInPublicForm: true },
  { id: 'base_email', name: 'email', label: 'Correo', type: 'email', required: true, isBase: true, showInPublicForm: true },
  { id: 'base_phone', name: 'phone', label: 'Celular', type: 'tel', required: true, isBase: true, showInPublicForm: true },
  { id: 'base_company', name: 'company', label: 'Empresa', type: 'text', required: true, isBase: true, showInPublicForm: true },
  { id: 'base_documentType', name: 'documentType', label: 'Tipo Documento', type: 'select', required: true, isBase: true, showInPublicForm: true,
    options: ['NIT', 'CC', 'Cédula Extranjería'] },
  { id: 'base_documentNumber', name: 'documentNumber', label: 'Número Documento', type: 'text', required: true, isBase: true, showInPublicForm: true,
    placeholder: 'Número de NIT / CC / Cédula' },
  { id: 'base_personType', name: 'personType', label: 'Tipo Persona', type: 'select', required: true, isBase: true, showInPublicForm: true,
    options: ['Persona Natural', 'Persona Jurídica'] },
];

// Tipos de campos disponibles
const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'textarea', label: 'Texto Largo' },
  { value: 'number', label: 'Número' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Teléfono' },
  { value: 'date', label: 'Fecha' },
  { value: 'select', label: 'Selección' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Opciones (Radio)' },
];

const FormBuilder = () => {
  const { state, actions } = useData();
  const { settings } = useSettings();
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [formTitle, setFormTitle] = useState('¡Hagamos que tus ideas despeguen!');
  const [formSubtitle, setFormSubtitle] = useState('Contáctanos y hablemos para hacerlas realidad.');
  const [submitText, setSubmitText] = useState('Enviar');
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Tab state: 'form' or 'whatsapp' - persisted in localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('formbuilder_active_tab');
    return saved === 'whatsapp' ? 'whatsapp' : 'form';
  });
  
  // Persist activeTab when it changes
  useEffect(() => {
    localStorage.setItem('formbuilder_active_tab', activeTab);
  }, [activeTab]);
  
  // Dynamic primary color
  const primaryColor = settings?.primaryColor || '#00b30c';
  
  // Código de país seleccionado para el campo de teléfono
  const [selectedCountryCode, setSelectedCountryCode] = useState('+57');
  
  // Campos base con su configuración de visibilidad
  const [baseFields, setBaseFields] = useState(DEFAULT_BASE_FIELDS);
  
  // Campos personalizados (nunca se borran, solo se activan/desactivan)
  const [customFields, setCustomFields] = useState([]);
  
  // Orden de todos los campos (IDs)
  const [fieldOrder, setFieldOrder] = useState([]);
  
  // Modal para agregar campo
  const [showAddField, setShowAddField] = useState(false);
  
  // Modal para editar campo
  const [showEditField, setShowEditField] = useState(false);
  const [editingField, setEditingField] = useState(null);
  
  // Drag state
  const [draggedFieldId, setDraggedFieldId] = useState(null);
  const [dragOverFieldId, setDragOverFieldId] = useState(null);
  
  // New field form
  const [newField, setNewField] = useState({
    label: '',
    type: 'text',
    required: false,
    options: '',
    placeholder: ''
  });

  // WhatsApp Widget Configuration
  const [whatsappConfig, setWhatsappConfig] = useState({
    enabled: false,
    phoneNumber: '',
    message: 'Hola, me gustaría más información',
    position: 'bottom-right',
    buttonColor: '#25D366',
    headerText: '¡Contáctanos por WhatsApp!',
    fields: [
      { name: 'name', label: 'Nombre', required: true, type: 'text' },
      { name: 'email', label: 'Email', required: true, type: 'email' },
      { name: 'phone', label: 'Teléfono', required: true, type: 'tel' }
    ],
    privacyPolicyEnabled: true,
    privacyPolicyUrl: '',
    privacyPolicyText: 'He leído y acepto la Política de Privacidad'
  });
  const [whatsappCopied, setWhatsappCopied] = useState(false);
  const [showWhatsappPreview, setShowWhatsappPreview] = useState(false);
  
  // Privacy Policy Configuration for Web Form
  const [privacyPolicyConfig, setPrivacyPolicyConfig] = useState({
    enabled: true,
    url: '',
    text: 'He leído y acepto la Política de Privacidad',
    linkText: 'Leer Políticas de Privacidad Aquí'
  });

  // Form Size Configuration
  const [formSize, setFormSize] = useState('normal'); // 'compact', 'normal', 'large'

  // Form Style Configuration
  const [formStyle, setFormStyle] = useState({
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    labelColor: '#ffffff',
    inputBgColor: '#ffffff',
    inputTextColor: '#333333',
    inputBorderColor: '#d1d5db',
    buttonColor: '#4ade80',
    buttonTextColor: '#000000',
    buttonOpacity: 100
  });

  // Embed code type: 'full' (complete HTML) or 'compact' (small script)
  const [embedCodeType, setEmbedCodeType] = useState('compact');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Load pipeline and its configuration
  useEffect(() => {
    if (state.pipelines.length > 0 && !selectedPipeline) {
      setSelectedPipeline(state.pipelines[0].id);
    }
  }, [state.pipelines, selectedPipeline]);

  // Load fields configuration when pipeline changes
  useEffect(() => {
    if (selectedPipeline) {
      const pipeline = state.pipelines.find(p => p.id === selectedPipeline);
      if (pipeline) {
        // Load base fields visibility configuration
        if (pipeline.baseFieldsConfig) {
          // Merge with defaults to ensure new base fields are always available
          const savedMap = {};
          pipeline.baseFieldsConfig.forEach(f => { savedMap[f.id] = f; });
          const merged = DEFAULT_BASE_FIELDS.map(df => savedMap[df.id] ? { ...df, ...savedMap[df.id] } : df);
          setBaseFields(merged);
        } else {
          setBaseFields(DEFAULT_BASE_FIELDS.map(f => ({ ...f })));
        }
        
        // Load custom fields
        if (pipeline.customFields) {
          setCustomFields(pipeline.customFields);
        } else {
          setCustomFields([]);
        }
        
        // Load field order
        if (pipeline.fieldOrder && pipeline.fieldOrder.length > 0) {
          setFieldOrder(pipeline.fieldOrder);
        } else {
          // Default order: base fields first, then custom
          const defaultOrder = [
            ...DEFAULT_BASE_FIELDS.map(f => f.id),
            ...(pipeline.customFields || []).map(f => f.id)
          ];
          setFieldOrder(defaultOrder);
        }
        
        // Load privacy policy configuration
        if (pipeline.privacyPolicyConfig) {
          setPrivacyPolicyConfig(pipeline.privacyPolicyConfig);
        } else {
          setPrivacyPolicyConfig({
            enabled: true,
            url: '',
            text: 'He leído y acepto la Política de Privacidad',
            linkText: 'Leer Políticas de Privacidad Aquí'
          });
        }
        
        // Load form style per pipeline
        if (pipeline.formStyleConfig) {
          setFormStyle({
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            labelColor: '#ffffff',
            inputBgColor: '#ffffff',
            inputTextColor: '#333333',
            inputBorderColor: '#d1d5db',
            buttonColor: '#4ade80',
            buttonTextColor: '#000000',
            buttonOpacity: 100,
            ...pipeline.formStyleConfig
          });
        } else {
          setFormStyle({
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            labelColor: '#ffffff',
            inputBgColor: '#ffffff',
            inputTextColor: '#333333',
            inputBorderColor: '#d1d5db',
            buttonColor: '#4ade80',
            buttonTextColor: '#000000',
            buttonOpacity: 100
          });
        }
      }
    }
  }, [selectedPipeline, state.pipelines]);

  // Generate unique ID for new fields
  const generateFieldId = () => {
    return `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Add new custom field
  const handleAddField = () => {
    if (!newField.label.trim()) {
      toast.error('El nombre del campo es requerido');
      return;
    }

    const fieldId = generateFieldId();
    const field = {
      id: fieldId,
      name: fieldId,
      label: newField.label,
      type: newField.type,
      required: newField.required,
      placeholder: newField.placeholder,
      options: newField.type === 'select' || newField.type === 'radio' 
        ? newField.options.split(',').map(o => o.trim()).filter(o => o)
        : [],
      isBase: false,
      isActive: true, // New fields are active by default
      createdAt: new Date().toISOString()
    };

    setCustomFields([...customFields, field]);
    setFieldOrder([...fieldOrder, fieldId]);
    setNewField({ label: '', type: 'text', required: false, options: '', placeholder: '' });
    setShowAddField(false);
    toast.success('Campo agregado');
  };

  // Toggle custom field active/inactive
  const toggleFieldActive = (fieldId) => {
    setCustomFields(customFields.map(f => 
      f.id === fieldId ? { ...f, isActive: !f.isActive } : f
    ));
  };

  // Edit custom field
  const handleEditField = (field) => {
    setEditingField({
      ...field,
      options: (field.options || []).join(', ')
    });
    setShowEditField(true);
  };

  const handleSaveEditField = () => {
    if (!editingField.label.trim()) {
      toast.error('El nombre del campo es requerido');
      return;
    }

    setCustomFields(customFields.map(f => 
      f.id === editingField.id 
        ? {
            ...f,
            label: editingField.label,
            type: editingField.type,
            required: editingField.required,
            placeholder: editingField.placeholder,
            options: editingField.type === 'select' || editingField.type === 'radio' 
              ? editingField.options.split(',').map(o => o.trim()).filter(o => o)
              : []
          }
        : f
    ));
    
    setShowEditField(false);
    setEditingField(null);
    toast.success('Campo actualizado');
  };

  // Toggle base field visibility in public form
  const toggleBaseFieldPublic = (fieldId) => {
    setBaseFields(baseFields.map(f => 
      f.id === fieldId ? { ...f, showInPublicForm: !f.showInPublicForm } : f
    ));
  };

  // Drag and Drop handlers
  const handleDragStart = (e, fieldId) => {
    setDraggedFieldId(fieldId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, fieldId) => {
    e.preventDefault();
    if (draggedFieldId !== fieldId) {
      setDragOverFieldId(fieldId);
    }
  };

  const handleDragLeave = () => {
    setDragOverFieldId(null);
  };

  const handleDrop = (e, targetFieldId) => {
    e.preventDefault();
    
    if (draggedFieldId && draggedFieldId !== targetFieldId) {
      const newOrder = [...fieldOrder];
      const draggedIndex = newOrder.indexOf(draggedFieldId);
      const targetIndex = newOrder.indexOf(targetFieldId);
      
      // Remove from old position
      newOrder.splice(draggedIndex, 1);
      // Insert at new position
      newOrder.splice(targetIndex, 0, draggedFieldId);
      
      setFieldOrder(newOrder);
    }
    
    setDraggedFieldId(null);
    setDragOverFieldId(null);
  };

  const handleDragEnd = () => {
    setDraggedFieldId(null);
    setDragOverFieldId(null);
  };

  // Save configuration to pipeline
  const handleSaveConfiguration = async () => {
    setSaving(true);
    try {
      await actions.updatePipeline(selectedPipeline, { 
        customFields,
        baseFieldsConfig: baseFields,
        fieldOrder,
        privacyPolicyConfig,
        formStyleConfig: formStyle
      });
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  // Get all fields in order
  const getOrderedFields = useCallback(() => {
    const allFields = [...baseFields, ...customFields];
    return fieldOrder
      .map(id => allFields.find(f => f.id === id))
      .filter(Boolean);
  }, [baseFields, customFields, fieldOrder]);

  // Get fields for public form (only visible base + active custom)
  const getPublicFormFields = useCallback(() => {
    const orderedFields = getOrderedFields();
    return orderedFields.filter(f => {
      if (f.isBase) return f.showInPublicForm;
      return f.isActive;
    }).map(f => ({
      ...f,
      label: f.customLabel || f.label,
      placeholder: f.customPlaceholder || f.placeholder || '',
    }));
  }, [getOrderedFields]);

  // Generate form HTML code
  const generateFormCode = () => {
    const fields = getPublicFormFields();
    
    // Use current domain for production compatibility
    const currentDomain = window.location.origin;
    
    // Check if CAPTCHA is enabled in settings
    const isCaptchaEnabled = settings?.enableRecaptcha && 
      settings?.recaptchaSiteKey && 
      String(settings.recaptchaSiteKey).trim().length > 10;
    
    const generateFieldHTML = (field) => {
      if (field.type === 'select') {
        return `      <div class="form-group">
        <label class="form-label" for="${field.name}">${field.label}${field.required ? ' *' : ''}</label>
        <select id="${field.name}" name="${field.name}" class="form-input" ${field.required ? 'required' : ''}>
          <option value="">Seleccionar...</option>
          ${(field.options || []).map(opt => `<option value="${opt}">${opt}</option>`).join('\n          ')}
        </select>
      </div>`;
      } else if (field.type === 'textarea') {
        return `      <div class="form-group">
        <label class="form-label" for="${field.name}">${field.label}${field.required ? ' *' : ''}</label>
        <textarea id="${field.name}" name="${field.name}" class="form-input form-textarea" rows="3" placeholder="${field.placeholder || field.label}" ${field.required ? 'required' : ''}></textarea>
      </div>`;
      } else if (field.type === 'checkbox') {
        return `      <div class="form-group form-checkbox">
        <input type="checkbox" id="${field.name}" name="${field.name}" ${field.required ? 'required' : ''} />
        <label class="form-label" for="${field.name}">${field.label}${field.required ? ' *' : ''}</label>
      </div>`;
      } else if (field.type === 'radio') {
        return `      <div class="form-group">
        <label class="form-label">${field.label}${field.required ? ' *' : ''}</label>
        <div class="form-radio-group">
          ${(field.options || []).map(opt => `<label class="form-radio"><input type="radio" name="${field.name}" value="${opt}" ${field.required ? 'required' : ''} /> ${opt}</label>`).join('\n          ')}
        </div>
      </div>`;
      } else if (field.type === 'tel') {
        // Generar opciones de código de país
        const countryOptions = COUNTRY_CODES.map(c => 
          `<option value="${c.code}" ${c.code === selectedCountryCode ? 'selected' : ''}>${c.flag} ${c.code}</option>`
        ).join('\n            ');
        
        return `      <div class="form-group">
        <label class="form-label" for="${field.name}">${field.label}${field.required ? ' *' : ''}</label>
        <div class="phone-input-group" style="display: flex; gap: 8px;">
          <select id="${field.name}_country" name="${field.name}_country" class="form-input country-code-select" style="width: 110px; flex-shrink: 0;">
            ${countryOptions}
          </select>
          <input type="tel" id="${field.name}" name="${field.name}" class="form-input" placeholder="${field.placeholder || '300 123 4567'}" ${field.required ? 'required' : ''} style="flex: 1;" />
        </div>
      </div>`;
      } else {
        return `      <div class="form-group">
        <label class="form-label" for="${field.name}">${field.label}${field.required ? ' *' : ''}</label>
        <input type="${field.type}" id="${field.name}" name="${field.name}" class="form-input" placeholder="${field.placeholder || field.label}" ${field.required ? 'required' : ''} />
      </div>`;
      }
    };

    // Google reCAPTCHA v2 script
    const recaptchaSiteKey = settings?.recaptchaSiteKey || '';
    const captchaScript = `
  <script src="https://www.google.com/recaptcha/api.js" async defer></script>
  <script>
    // Google reCAPTCHA System
    (function() {
      let captchaVerified = false;
      
      // Called when reCAPTCHA is completed successfully
      window.onRecaptchaSuccess = function(token) {
        captchaVerified = true;
        window.recaptchaToken = token;
        updateSubmitButton();
      };
      
      // Called when reCAPTCHA expires
      window.onRecaptchaExpired = function() {
        captchaVerified = false;
        window.recaptchaToken = null;
        updateSubmitButton();
      };
      
      function updateSubmitButton() {
        const btn = document.querySelector('.form-button');
        if (btn) {
          btn.disabled = !captchaVerified;
          btn.style.opacity = captchaVerified ? '1' : '0.5';
          btn.style.cursor = captchaVerified ? 'pointer' : 'not-allowed';
        }
      }
      
      function isVerified() { return captchaVerified; }
      function getToken() { return window.recaptchaToken || ''; }
      
      window.CaptchaSystem = { 
        isVerified: isVerified, 
        getToken: getToken,
        render: function() {},
        toggle: function() {},
        verify: function() {},
        refresh: function() { if(window.grecaptcha) grecaptcha.reset(); }
      };
      
      // Initial state
      document.addEventListener('DOMContentLoaded', updateSubmitButton);
    })();
  </script>`;

    // Script for when CAPTCHA is disabled - button works directly
    const noCaptchaScript = `
  <script>
    // No CAPTCHA - Direct submission
    window.CaptchaSystem = { 
      isVerified: function() { return true; },
      getToken: function() { return ''; },
      render: function() {},
      toggle: function() {},
      verify: function() {},
      refresh: function() {}
    };
  </script>`;

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${formTitle}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: ${formStyle.fontFamily}; background: transparent; padding: ${formSize === 'compact' ? '1rem' : formSize === 'large' ? '2rem' : '1.5rem'}; }
    .form-container { max-width: 600px; margin: 0 auto; padding: ${formSize === 'compact' ? '1.25rem' : formSize === 'large' ? '2.5rem' : '2rem'}; background: ${formStyle.backgroundColor}; border-radius: 16px; ${formStyle.backgroundColor.includes('rgba') ? 'backdrop-filter: blur(10px);' : ''} box-sizing: border-box; overflow: hidden; }
    * { box-sizing: border-box; }
    .form-title { font-size: ${formSize === 'compact' ? '1.5rem' : formSize === 'large' ? '2.25rem' : '1.875rem'}; font-weight: 700; color: ${formStyle.labelColor}; margin-bottom: 0.75rem; text-align: center; }
    .form-title-divider { width: 60px; height: 3px; background: ${formStyle.buttonColor}; margin: 0 auto 0.75rem; border-radius: 2px; }
    .form-subtitle { font-size: ${formSize === 'compact' ? '0.875rem' : formSize === 'large' ? '1.125rem' : '1rem'}; font-weight: 500; color: ${formStyle.labelColor}; margin-bottom: ${formSize === 'compact' ? '1.25rem' : '1.75rem'}; text-align: center; opacity: 0.9; }
    .form-row { display: grid; grid-template-columns: repeat(2, 1fr); gap: ${formSize === 'compact' ? '0.75rem' : '1rem'}; }
    .form-row.single { grid-template-columns: 1fr; }
    .form-group { margin-bottom: ${formSize === 'compact' ? '0.75rem' : formSize === 'large' ? '1.5rem' : '1rem'}; }
    .form-label { display: block; font-size: ${formSize === 'compact' ? '0.75rem' : '0.875rem'}; font-weight: 500; color: ${formStyle.labelColor}; margin-bottom: 0.375rem; }
    .form-input { width: 100%; padding: ${formSize === 'compact' ? '0.5rem 0.75rem' : formSize === 'large' ? '0.875rem 1rem' : '0.625rem 0.875rem'}; border: 1.5px solid ${formStyle.inputBorderColor || '#d1d5db'}; border-radius: 8px; font-size: ${formSize === 'compact' ? '0.8rem' : '0.875rem'}; background: ${formStyle.inputBgColor}; color: ${formStyle.inputTextColor}; transition: all 0.2s; box-sizing: border-box; max-width: 100%; }
    .form-input:focus { outline: none; box-shadow: 0 0 0 3px ${formStyle.buttonColor}44; }
    .form-input::placeholder { color: #9ca3af; }
    .form-textarea { min-height: 80px; resize: vertical; }
    .form-checkbox { display: flex; align-items: center; gap: 0.5rem; }
    .form-checkbox input { width: auto; accent-color: ${formStyle.buttonColor}; }
    .form-checkbox .form-label { margin-bottom: 0; }
    .form-radio-group { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 0.5rem; }
    .form-radio { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: ${formStyle.labelColor}; }
    .form-radio input { accent-color: ${formStyle.buttonColor}; }
    .phone-input-group { display: flex; gap: 8px; align-items: stretch; }
    .country-code-select { width: 100px !important; flex-shrink: 0; padding: ${formSize === 'compact' ? '0.5rem' : '0.625rem'}; font-size: 0.8rem; cursor: pointer; }
    .form-button { width: 100%; padding: ${formSize === 'compact' ? '0.625rem' : formSize === 'large' ? '1rem' : '0.875rem'}; background: ${formStyle.buttonColor}; color: ${formStyle.buttonTextColor}; border: none; border-radius: 8px; font-size: ${formSize === 'compact' ? '0.875rem' : '1rem'}; font-weight: 600; cursor: pointer; margin-top: ${formSize === 'compact' ? '0.75rem' : '1rem'}; transition: all 0.2s; opacity: ${(formStyle.buttonOpacity || 100) / 100}; }
    .form-button:hover:not(:disabled) { filter: brightness(1.1); transform: translateY(-1px); }
    .form-button:disabled { opacity: 0.5; cursor: not-allowed; }
    .form-success { padding: 1.5rem; background: #dcfce7; color: #166534; border-radius: 8px; text-align: center; display: none; }
    
    /* reCAPTCHA wrapper */
    .recaptcha-wrapper { margin-top: 1rem; display: flex; justify-content: center; }
    .privacy-policy { display: flex; align-items: flex-start; gap: 0.5rem; margin-top: 1rem; margin-bottom: 0.5rem; }
    .privacy-policy input[type="checkbox"] { margin-top: 0.25rem; width: 1rem; height: 1rem; cursor: pointer; accent-color: ${formStyle.buttonColor}; }
    .privacy-policy label { font-size: 0.8rem; color: ${formStyle.labelColor}; cursor: pointer; }
    .privacy-policy a { color: ${formStyle.buttonColor}; text-decoration: underline; }
    .privacy-link { display: block; text-align: center; margin-top: 0.75rem; font-size: 0.7rem; color: ${formStyle.buttonColor}; text-decoration: none; }
    .privacy-link:hover { text-decoration: underline; }
    @media (max-width: 480px) { .form-row { grid-template-columns: 1fr; } }
  </style>
</head>
<body>
  <div class="form-container">
    <h2 class="form-title">${formTitle}</h2>
    ${formSubtitle ? `<div class="form-title-divider"></div><p class="form-subtitle">${formSubtitle}</p>` : ''}
    <form id="datacrm-form">
${fields.map(generateFieldHTML).join('\n')}
      
      ${isCaptchaEnabled ? `<!-- Google reCAPTCHA -->
      <div class="recaptcha-wrapper" style="margin-top: 1rem; display: flex; justify-content: center;">
        <div class="g-recaptcha" data-sitekey="${recaptchaSiteKey}" data-callback="onRecaptchaSuccess" data-expired-callback="onRecaptchaExpired" data-theme="dark"></div>
      </div>` : '<!-- CAPTCHA disabled in settings -->'}
      
      ${privacyPolicyConfig.enabled ? `<!-- Privacy Policy Checkbox -->
      <div class="privacy-policy">
        <input type="checkbox" id="privacy-policy" name="privacy_policy" required />
        <label for="privacy-policy">${privacyPolicyConfig.text}*</label>
      </div>` : ''}
      
      <!-- SECURITY: Honeypot field (hidden trap for bots) -->
      <input type="text" name="website_url" class="honeypot-field" tabindex="-1" autocomplete="off" aria-hidden="true" style="position:absolute;left:-9999px;opacity:0;height:0;width:0;">
      
      <button type="submit" class="form-button" ${isCaptchaEnabled ? 'disabled' : ''}>${submitText}</button>
      
      ${privacyPolicyConfig.enabled && privacyPolicyConfig.url ? `<a href="${privacyPolicyConfig.url}" target="_blank" class="privacy-link">*${privacyPolicyConfig.linkText}</a>` : ''}
    </form>
    <div id="success-message" class="form-success">¡Gracias! Hemos recibido tu información.</div>
  </div>
  ${isCaptchaEnabled ? captchaScript : noCaptchaScript}
  <script>
    document.getElementById('datacrm-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      
      // === SECURITY: Check honeypot (bot detection) ===
      const honeypot = e.target.querySelector('input[name="website_url"]');
      if (honeypot && honeypot.value) {
        console.warn('Bot detected via honeypot');
        e.target.style.display = 'none';
        document.getElementById('success-message').style.display = 'block';
        return;
      }
      
      // Verify CAPTCHA before submit
      if (!window.CaptchaSystem.isVerified()) {
        alert('Por favor, completa la verificación de seguridad');
        return;
      }
      
      // === SECURITY: Allowlist - define allowed field names ===
      const allowedFields = [${fields.map(f => `'${f.name}'`).join(', ')}, 'phone_country', 'privacy_policy'];
      
      // === SECURITY: Only extract allowed fields ===
      const formData = new FormData(e.target);
      const data = {};
      allowedFields.forEach(fieldName => {
        const value = formData.get(fieldName);
        if (value !== null && value !== undefined) {
          // Limit field length to prevent DoS
          data[fieldName] = String(value).substring(0, 5000);
        }
      });
      
      // Combinar código de país con número de teléfono
      if (data.phone && data.phone_country) {
        data.phone = data.phone_country + ' ' + data.phone;
        delete data.phone_country;
      }
      
      if (window.parent !== window) {
        // SECURITY: Use current domain for iframe communication
        const allowedOrigin = '${currentDomain}';
        try {
          window.parent.postMessage({
            type: 'DATACRM_FORM_CAPTURE',
            pipelineId: '${selectedPipeline}',
            formData: data
          }, allowedOrigin || window.location.origin);
        } catch (e) {
          // Fallback to same origin only
          window.parent.postMessage({
            type: 'DATACRM_FORM_CAPTURE',
            pipelineId: '${selectedPipeline}',
            formData: data
          }, window.location.origin);
        }
      }
      
      try {
        // Get tracking data
        const trackingData = window.DataCRMTracking ? window.DataCRMTracking.getDataWithHoneypot(e.target) : {};
        
        // Get reCAPTCHA token
        const captchaToken = window.CaptchaSystem.getToken ? window.CaptchaSystem.getToken() : '';
        
        const response = await fetch('${currentDomain}/api/form-capture', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            form_id: 'form-${selectedPipeline}',
            pipeline_id: '${selectedPipeline}',
            data: data,
            trackingData: trackingData,
            captcha_token: captchaToken
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 429) {
            alert('Has enviado demasiadas solicitudes. Intenta de nuevo en unos minutos.');
            return;
          }
          throw new Error(errorData.detail || 'Error al enviar');
        }
      } catch (error) {
        console.log('Backend capture failed:', error.message);
        if (error.message.includes('429') || error.message.includes('demasiadas')) {
          alert('Has enviado demasiadas solicitudes. Intenta de nuevo en unos minutos.');
          return;
        }
      }
      
      e.target.style.display = 'none';
      document.getElementById('success-message').style.display = 'block';
    });
  </script>
  
  <!-- DataCRM Tracking Script -->
  <script>
    (function() {
      'use strict';
      
      var pageLoadTime = Date.now();
      var maxScrollDepth = 0;
      var formStartTime = null;
      var emailPasted = false;
      var autofillDetected = false;
      
      // === SECURITY: Sanitize function to prevent XSS ===
      function sanitize(str) {
        if (!str) return '';
        return String(str)
          .replace(/[<>'"\\\\&]/g, '') // Remove dangerous characters
          .replace(/javascript:/gi, '') // Remove JS protocol
          .replace(/on\\w+=/gi, '') // Remove event handlers
          .substring(0, 200); // Limit length
      }
      
      // Get URL parameters (SANITIZED)
      function getUrlParams() {
        var params = new URLSearchParams(window.location.search);
        return {
          utm_source: sanitize(params.get('utm_source') || ''),
          utm_medium: sanitize(params.get('utm_medium') || ''),
          utm_campaign: sanitize(params.get('utm_campaign') || ''),
          utm_term: sanitize(params.get('utm_term') || ''),
          utm_content: sanitize(params.get('utm_content') || ''),
          gclid: sanitize(params.get('gclid') || ''),
          fbclid: sanitize(params.get('fbclid') || '')
        };
      }
      
      // Detect traffic channel from referrer
      function detectChannel(referrer, utms) {
        var ref = (referrer || '').toLowerCase();
        
        // If has UTM source, use it
        if (utms.utm_source) {
          var source = utms.utm_source.toLowerCase();
          var medium = (utms.utm_medium || '').toLowerCase();
          
          if (utms.gclid || source === 'google' && medium === 'cpc') return { channel: 'Google Ads', type: 'paid' };
          if (utms.fbclid || source === 'facebook' && medium === 'cpc') return { channel: 'Facebook Ads', type: 'paid' };
          if (source === 'google') return { channel: 'Google ' + (medium || 'Referral'), type: medium === 'organic' ? 'organic' : 'referral' };
          if (source === 'bing') return { channel: 'Bing ' + (medium || 'Referral'), type: medium === 'organic' ? 'organic' : 'referral' };
          if (source === 'facebook' || source === 'instagram' || source === 'twitter' || source === 'linkedin' || source === 'tiktok') {
            return { channel: source.charAt(0).toUpperCase() + source.slice(1) + ' ' + (medium || 'Social'), type: 'social' };
          }
          return { channel: utms.utm_source + (medium ? ' - ' + medium : ''), type: 'campaign' };
        }
        
        // If has click IDs
        if (utms.gclid) return { channel: 'Google Ads', type: 'paid' };
        if (utms.fbclid) return { channel: 'Facebook Ads', type: 'paid' };
        
        // No referrer = Direct
        if (!ref || ref === '' || ref === window.location.origin.toLowerCase()) {
          return { channel: 'Tráfico Directo', type: 'direct' };
        }
        
        // Search engines - Organic
        if (ref.includes('google.com') || ref.includes('google.co')) return { channel: 'Google Orgánico', type: 'organic' };
        if (ref.includes('bing.com')) return { channel: 'Bing Orgánico', type: 'organic' };
        if (ref.includes('yahoo.com')) return { channel: 'Yahoo Orgánico', type: 'organic' };
        if (ref.includes('duckduckgo.com')) return { channel: 'DuckDuckGo Orgánico', type: 'organic' };
        if (ref.includes('ecosia.org')) return { channel: 'Ecosia Orgánico', type: 'organic' };
        if (ref.includes('baidu.com')) return { channel: 'Baidu Orgánico', type: 'organic' };
        if (ref.includes('yandex.')) return { channel: 'Yandex Orgánico', type: 'organic' };
        
        // AI Platforms
        if (ref.includes('chat.openai.com') || ref.includes('chatgpt.com')) return { channel: 'ChatGPT (IA)', type: 'ai' };
        if (ref.includes('perplexity.ai')) return { channel: 'Perplexity (IA)', type: 'ai' };
        if (ref.includes('claude.ai') || ref.includes('anthropic.com')) return { channel: 'Claude (IA)', type: 'ai' };
        if (ref.includes('bard.google.com') || ref.includes('gemini.google.com')) return { channel: 'Gemini (IA)', type: 'ai' };
        if (ref.includes('copilot.microsoft.com') || ref.includes('bing.com/chat')) return { channel: 'Copilot (IA)', type: 'ai' };
        if (ref.includes('you.com')) return { channel: 'You.com (IA)', type: 'ai' };
        if (ref.includes('phind.com')) return { channel: 'Phind (IA)', type: 'ai' };
        
        // Social Media
        if (ref.includes('facebook.com') || ref.includes('fb.com')) return { channel: 'Facebook Social', type: 'social' };
        if (ref.includes('instagram.com')) return { channel: 'Instagram Social', type: 'social' };
        if (ref.includes('twitter.com') || ref.includes('x.com')) return { channel: 'Twitter/X Social', type: 'social' };
        if (ref.includes('linkedin.com')) return { channel: 'LinkedIn Social', type: 'social' };
        if (ref.includes('tiktok.com')) return { channel: 'TikTok Social', type: 'social' };
        if (ref.includes('youtube.com')) return { channel: 'YouTube', type: 'social' };
        if (ref.includes('pinterest.com')) return { channel: 'Pinterest Social', type: 'social' };
        if (ref.includes('reddit.com')) return { channel: 'Reddit Social', type: 'social' };
        if (ref.includes('whatsapp.com') || ref.includes('wa.me')) return { channel: 'WhatsApp', type: 'social' };
        if (ref.includes('t.me') || ref.includes('telegram.')) return { channel: 'Telegram', type: 'social' };
        
        // Email providers (likely from email campaigns)
        if (ref.includes('mail.google.com') || ref.includes('outlook.live.com') || ref.includes('mail.yahoo.com')) {
          return { channel: 'Email', type: 'email' };
        }
        
        // Extract domain for unknown referrers
        try {
          var domain = new URL(referrer).hostname.replace('www.', '');
          return { channel: 'Referido: ' + domain, type: 'referral' };
        } catch(e) {
          return { channel: 'Canal no identificado', type: 'unknown' };
        }
      }
      
      // Store first URL and detect initial channel
      var initialReferrer = document.referrer;
      var initialUtms = getUrlParams();
      var detectedChannel = detectChannel(initialReferrer, initialUtms);
      
      if (!sessionStorage.getItem('datacrm_first_url')) {
        sessionStorage.setItem('datacrm_first_url', window.location.href);
        sessionStorage.setItem('datacrm_utms', JSON.stringify(initialUtms));
        sessionStorage.setItem('datacrm_channel', JSON.stringify(detectedChannel));
        sessionStorage.setItem('datacrm_referrer', initialReferrer);
      }
      
      // Visit counter
      var visitCount = parseInt(localStorage.getItem('datacrm_visits') || '0') + 1;
      localStorage.setItem('datacrm_visits', visitCount);
      
      // Device type detection
      function getDeviceType() {
        var w = window.innerWidth;
        return w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop';
      }
      
      // Track scroll
      window.addEventListener('scroll', function() {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var docHeight = document.documentElement.scrollHeight - window.innerHeight;
        var scrollPct = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
        if (scrollPct > maxScrollDepth) maxScrollDepth = scrollPct;
      }, { passive: true });
      
      // Detect form interaction start
      document.addEventListener('focusin', function(e) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
          if (!formStartTime) formStartTime = Date.now();
        }
      });
      
      // Detect email paste
      document.addEventListener('paste', function(e) {
        if (e.target.type === 'email' || e.target.name === 'email' || e.target.id === 'email') {
          emailPasted = true;
        }
      });
      
      // Check autofill
      setTimeout(function() {
        document.querySelectorAll('input').forEach(function(input) {
          try { if (input.matches(':-webkit-autofill')) autofillDetected = true; } catch(e) {}
        });
      }, 1000);
      
      // Get all tracking data
      function getTrackingData() {
        var storedUtms = {};
        try { storedUtms = JSON.parse(sessionStorage.getItem('datacrm_utms') || '{}'); } catch(e) {}
        
        var currentUtms = getUrlParams();
        var utms = {
          utm_source: currentUtms.utm_source || storedUtms.utm_source || '',
          utm_medium: currentUtms.utm_medium || storedUtms.utm_medium || '',
          utm_campaign: currentUtms.utm_campaign || storedUtms.utm_campaign || '',
          utm_term: currentUtms.utm_term || storedUtms.utm_term || '',
          utm_content: currentUtms.utm_content || storedUtms.utm_content || '',
          gclid: currentUtms.gclid || storedUtms.gclid || '',
          fbclid: currentUtms.fbclid || storedUtms.fbclid || ''
        };
        
        var timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
        var formFillDuration = formStartTime ? Math.round((Date.now() - formStartTime) / 1000) : 0;
        
        // Detect email domain type
        var emailInput = document.querySelector('input[type="email"], input[name="email"]');
        var emailDomain = '';
        var emailDomainType = 'desconocido';
        if (emailInput && emailInput.value) {
          var email = emailInput.value;
          var atPos = email.indexOf('@');
          if (atPos > 0) {
            emailDomain = email.substring(atPos + 1).toLowerCase();
            var genericDomains = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'live.com', 'icloud.com', 'mail.com'];
            emailDomainType = genericDomains.includes(emailDomain) ? 'generico' : 'corporativo';
          }
        }
        
        // Get stored channel info
        var storedChannel = {};
        try { storedChannel = JSON.parse(sessionStorage.getItem('datacrm_channel') || '{}'); } catch(e) {}
        var storedReferrer = sessionStorage.getItem('datacrm_referrer') || '';
        
        // Use stored channel or detect new one
        var channelInfo = storedChannel.channel ? storedChannel : detectChannel(storedReferrer || document.referrer, utms);
        
        return {
          // Channel info (NEW)
          channel: channelInfo.channel || 'Canal no identificado',
          channel_type: channelInfo.type || 'unknown',
          
          // UTM params
          utm_source: utms.utm_source,
          utm_medium: utms.utm_medium,
          utm_campaign: utms.utm_campaign,
          utm_term: utms.utm_term,
          utm_content: utms.utm_content,
          gclid: utms.gclid,
          fbclid: utms.fbclid,
          referrer_url: storedReferrer || document.referrer || '',
          
          // Technical
          user_agent: navigator.userAgent,
          device_type: getDeviceType(),
          browser_language: navigator.language || '',
          screen_resolution: screen.width + 'x' + screen.height,
          connection_type: (navigator.connection && navigator.connection.effectiveType) || 'unknown',
          
          // Behavior
          first_touch_url: sessionStorage.getItem('datacrm_first_url') || window.location.href,
          conversion_url: window.location.href,
          visit_count: visitCount,
          scroll_depth_pct: maxScrollDepth,
          saw_whole_page: maxScrollDepth >= 90,
          time_on_page_sec: timeOnPage,
          
          // Form quality
          form_fill_duration: formFillDuration,
          email_pasted: emailPasted,
          used_autofill: autofillDetected,
          is_bot_honeypot: false,
          email_domain_type: emailDomainType,
          
          // Context
          user_local_time: new Date().toISOString()
        };
      }
      
      // Check honeypot (SECURITY: bot detection)
      function checkHoneypot(form) {
        // Check the official honeypot field
        var honeypot = form.querySelector('input[name="website_url"]');
        if (honeypot && honeypot.value && honeypot.value.trim() !== '') {
          return true;
        }
        // Also check legacy honeypot selectors for backwards compatibility
        var legacySelectors = ['input[name="website"]', 'input[name="fax"]', 'input.honeypot', 'input.honeypot-field'];
        for (var i = 0; i < legacySelectors.length; i++) {
          var h = form.querySelector(legacySelectors[i]);
          if (h && h.value && h.value.trim() !== '') return true;
        }
        return false;
      }
      
      window.DataCRMTracking = {
        getData: getTrackingData,
        checkHoneypot: checkHoneypot,
        getDataWithHoneypot: function(form) {
          var data = getTrackingData();
          if (form) data.is_bot_honeypot = checkHoneypot(form);
          return data;
        }
      };
      
      console.log('[DataCRM Tracking] Loaded');
    })();
  </script>
</body>
</html>`;
  };

  // Generate compact embed code (iframe-based)
  const generateCompactEmbedCode = () => {
    const currentDomain = window.location.origin;
    const formConfig = {
      pipelineId: selectedPipeline,
      fields: getPublicFormFields().map(f => ({
        name: f.name,
        label: f.label,
        type: f.type,
        required: f.required,
        placeholder: f.placeholder,
        options: f.options
      })),
      style: formStyle,
      size: formSize,
      title: formTitle,
      subtitle: formSubtitle,
      submitText: submitText,
      privacyPolicy: privacyPolicyConfig,
      countryCode: selectedCountryCode
    };
    
    const configBase64 = btoa(encodeURIComponent(JSON.stringify(formConfig)));
    
    return `<!-- DataCRM Form Embed v2 (Secure) -->
<div id="datacrm-form-container" style="max-width:600px;margin:0 auto;"></div>
<script>
(function(){
  'use strict';
  var c='${configBase64}',
      u='${currentDomain}',
      container=document.getElementById('datacrm-form-container');
  if(!container)return;
  
  // Parse config safely
  var cfg;
  try{cfg=JSON.parse(decodeURIComponent(atob(c)));}catch(e){console.error('Invalid form config');return;}
  
  var s=cfg.style||{},sz=cfg.size||'normal';
  var pad=sz==='compact'?'1.25rem':sz==='large'?'2.5rem':'2rem',
      gap=sz==='compact'?'0.75rem':'1rem',
      inp=sz==='compact'?'0.5rem 0.75rem':'0.625rem 0.875rem';
  
  // Inject styles (safe - no user data)
  var css=\`
    #dcf *{box-sizing:border-box;margin:0;padding:0}
    #dcf{font-family:\${s.fontFamily||'system-ui'};background:\${s.backgroundColor||'rgba(0,0,0,0.85)'};padding:\${pad};border-radius:16px;\${s.backgroundColor&&s.backgroundColor.includes('rgba')?'backdrop-filter:blur(10px);':''}}
    #dcf h2{font-size:1.875rem;font-weight:700;color:\${s.labelColor||'#fff'};margin-bottom:0.75rem;text-align:center}
    #dcf .divider{width:60px;height:3px;background:\${s.buttonColor||'#4ade80'};margin:0 auto 0.75rem;border-radius:2px}
    #dcf .subtitle{font-size:1rem;font-weight:500;color:\${s.labelColor||'#fff'};margin-bottom:1.75rem;text-align:center;opacity:0.9}
    #dcf .row{display:grid;grid-template-columns:repeat(2,1fr);gap:\${gap}}
    #dcf .field{margin-bottom:\${gap}}
    #dcf label{display:block;font-size:0.875rem;font-weight:500;color:\${s.labelColor||'#fff'};margin-bottom:0.375rem}
    #dcf *{box-sizing:border-box}
    #dcf input,#dcf select,#dcf textarea{width:100%;max-width:100%;padding:\${inp};border:1.5px solid \${s.inputBorderColor||'#d1d5db'};border-radius:8px;font-size:0.875rem;background:\${s.inputBgColor||'#fff'};color:\${s.inputTextColor||'#333'};box-sizing:border-box}
    #dcf input:focus,#dcf select:focus,#dcf textarea:focus{outline:none;box-shadow:0 0 0 3px \${s.buttonColor||'#4ade80'}44}
    #dcf textarea{min-height:80px;resize:vertical}
    #dcf button[type="submit"]{width:100%;padding:0.875rem;background:\${s.buttonColor||'#4ade80'};color:\${s.buttonTextColor||'#000'};border:none;border-radius:8px;font-size:1rem;font-weight:600;cursor:pointer;margin-top:1rem;opacity:\${(s.buttonOpacity||100)/100}}
    #dcf button[type="submit"]:hover{filter:brightness(1.1)}
    #dcf .success-msg{padding:1.5rem;background:#dcfce7;color:#166534;border-radius:8px;text-align:center;display:none}
    #dcf .privacy{display:flex;align-items:flex-start;gap:0.5rem;margin-top:1rem}
    #dcf .privacy input{width:auto}
    #dcf .privacy label{margin:0;font-size:0.8rem}
    #dcf .hp-trap{position:absolute;left:-9999px;opacity:0}
    @media(max-width:480px){#dcf .row{grid-template-columns:1fr}}
  \`;
  var styleEl=document.createElement('style');
  styleEl.textContent=css;
  document.head.appendChild(styleEl);
  
  // === SECURITY: Build DOM using createElement (prevents XSS) ===
  var wrapper=document.createElement('div');
  wrapper.id='dcf';
  
  // Title (safe: textContent escapes HTML)
  var title=document.createElement('h2');
  title.textContent=cfg.title||'Formulario';
  wrapper.appendChild(title);
  
  // Subtitle with divider
  if(cfg.subtitle){
    var divider=document.createElement('div');
    divider.className='divider';
    wrapper.appendChild(divider);
    var sub=document.createElement('p');
    sub.className='subtitle';
    sub.textContent=cfg.subtitle;
    wrapper.appendChild(sub);
  }
  
  // Form
  var form=document.createElement('form');
  form.id='dcf-form';
  
  // Fields container
  var row=document.createElement('div');
  row.className='row';
  
  // Allowed field names (SECURITY: Allowlist)
  var allowedFieldNames=[];
  
  // Build fields safely
  (cfg.fields||[]).forEach(function(f){
    if(!f.name||!f.type)return;
    
    // Sanitize field name (alphanumeric and underscore only)
    var safeName=String(f.name).replace(/[^a-zA-Z0-9_]/g,'').substring(0,50);
    if(!safeName)return;
    allowedFieldNames.push(safeName);
    
    var fieldDiv=document.createElement('div');
    fieldDiv.className='field';
    
    // Label (safe: textContent)
    var label=document.createElement('label');
    label.textContent=(f.label||safeName)+(f.required?' *':'');
    label.setAttribute('for','dcf-'+safeName);
    fieldDiv.appendChild(label);
    
    // Input element
    var input;
    if(f.type==='select'){
      input=document.createElement('select');
      input.name=safeName;
      input.id='dcf-'+safeName;
      if(f.required)input.required=true;
      var defaultOpt=document.createElement('option');
      defaultOpt.value='';
      defaultOpt.textContent='Seleccionar...';
      input.appendChild(defaultOpt);
      (f.options||[]).forEach(function(opt){
        var option=document.createElement('option');
        option.value=String(opt).substring(0,200);
        option.textContent=String(opt).substring(0,200);
        input.appendChild(option);
      });
    }else if(f.type==='textarea'){
      input=document.createElement('textarea');
      input.name=safeName;
      input.id='dcf-'+safeName;
      input.rows=3;
      input.placeholder=String(f.placeholder||f.label||'').substring(0,100);
      if(f.required)input.required=true;
    }else if(f.type==='tel'){
      // Phone with country code
      var phoneWrapper=document.createElement('div');
      phoneWrapper.style.cssText='display:flex;gap:8px';
      
      var countrySelect=document.createElement('select');
      countrySelect.name=safeName+'_country';
      countrySelect.style.width='90px';
      ['+57','+1','+52','+34','+54','+56','+51','+593','+58'].forEach(function(code){
        var opt=document.createElement('option');
        opt.value=code;
        opt.textContent=code;
        if(code===(cfg.countryCode||'+57'))opt.selected=true;
        countrySelect.appendChild(opt);
      });
      phoneWrapper.appendChild(countrySelect);
      
      input=document.createElement('input');
      input.type='tel';
      input.name=safeName;
      input.id='dcf-'+safeName;
      input.placeholder=String(f.placeholder||'300 123 4567').substring(0,50);
      input.style.flex='1';
      if(f.required)input.required=true;
      phoneWrapper.appendChild(input);
      
      fieldDiv.appendChild(phoneWrapper);
      row.appendChild(fieldDiv);
      allowedFieldNames.push(safeName+'_country');
      return;
    }else{
      input=document.createElement('input');
      input.type=['text','email','number','date','url'].includes(f.type)?f.type:'text';
      input.name=safeName;
      input.id='dcf-'+safeName;
      input.placeholder=String(f.placeholder||f.label||'').substring(0,100);
      if(f.required)input.required=true;
    }
    
    fieldDiv.appendChild(input);
    row.appendChild(fieldDiv);
  });
  
  form.appendChild(row);
  
  // === SECURITY: Honeypot field (hidden trap for bots) ===
  var honeypot=document.createElement('input');
  honeypot.type='text';
  honeypot.name='website_url';
  honeypot.className='hp-trap';
  honeypot.tabIndex=-1;
  honeypot.autocomplete='off';
  honeypot.setAttribute('aria-hidden','true');
  form.appendChild(honeypot);
  
  // Privacy policy checkbox
  if(cfg.privacyPolicy&&cfg.privacyPolicy.enabled){
    var privacyDiv=document.createElement('div');
    privacyDiv.className='privacy';
    
    var checkbox=document.createElement('input');
    checkbox.type='checkbox';
    checkbox.name='privacy_accepted';
    checkbox.id='dcf-privacy';
    checkbox.required=true;
    privacyDiv.appendChild(checkbox);
    
    var privacyLabel=document.createElement('label');
    privacyLabel.setAttribute('for','dcf-privacy');
    privacyLabel.textContent=(cfg.privacyPolicy.text||'Acepto la política de privacidad')+'*';
    privacyDiv.appendChild(privacyLabel);
    
    form.appendChild(privacyDiv);
    allowedFieldNames.push('privacy_accepted');
  }
  
  // Submit button
  var submitBtn=document.createElement('button');
  submitBtn.type='submit';
  submitBtn.textContent=cfg.submitText||'Enviar';
  form.appendChild(submitBtn);
  
  wrapper.appendChild(form);
  
  // Success message
  var successMsg=document.createElement('div');
  successMsg.className='success-msg';
  successMsg.id='dcf-success';
  successMsg.textContent='¡Gracias! Hemos recibido tu información.';
  wrapper.appendChild(successMsg);
  
  // Append to container (safe: built with DOM API)
  container.appendChild(wrapper);
  
  // === SECURITY: Sanitize function for UTMs ===
  function sanitize(str){
    if(!str)return'';
    return String(str).replace(/[<>'"\\\\&]/g,'').substring(0,200);
  }
  
  // Get UTM params safely
  function getUTMs(){
    var params={};
    try{
      var search=new URLSearchParams(window.location.search);
      ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'].forEach(function(key){
        var val=search.get(key);
        if(val)params[key]=sanitize(val);
      });
    }catch(e){}
    return params;
  }
  
  // Form submit handler
  form.addEventListener('submit',function(e){
    e.preventDefault();
    
    // === SECURITY: Check honeypot (bot detection) ===
    var hpValue=form.querySelector('input[name="website_url"]');
    if(hpValue&&hpValue.value){
      console.warn('Bot detected');
      successMsg.style.display='block';
      form.style.display='none';
      return;
    }
    
    // === SECURITY: Allowlist - only extract known fields ===
    var formData=new FormData(form);
    var data={};
    allowedFieldNames.forEach(function(name){
      var val=formData.get(name);
      if(val!==null&&val!==undefined){
        data[name]=String(val).substring(0,5000);
      }
    });
    
    // Build tracking data
    var utms=getUTMs();
    var trackingData={
      channel:'Formulario Web',
      channel_type:'form',
      source_widget:'Compact Form',
      device_type:window.innerWidth<768?'mobile':'desktop',
      conversion_url:sanitize(window.location.href).substring(0,200),
      referrer_url:sanitize(document.referrer).substring(0,200)
    };
    Object.keys(utms).forEach(function(k){trackingData[k]=utms[k];});
    
    // Submit to form-capture endpoint with correct format
    fetch(u+'/api/form-capture',{
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({
        form_id:'form-'+cfg.pipelineId,
        pipeline_id:cfg.pipelineId,
        data:data,
        trackingData:trackingData
      })
    })
    .then(function(r){
      if(!r.ok){
        if(r.status===429){alert('Demasiadas solicitudes. Intenta de nuevo en unos minutos.');return;}
        throw new Error('Server error');
      }
      form.style.display='none';
      successMsg.style.display='block';
    })
    .catch(function(err){
      if(String(err).includes('429'))return;
      alert('Error al enviar. Por favor intenta de nuevo.');
    });
  });
})();
</script>`;
  };

  const handleCopyCode = () => {
    const code = embedCodeType === 'compact' ? generateCompactEmbedCode() : generateFormCode();
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Código copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const pipeline = state.pipelines.find(p => p.id === selectedPipeline);
  const orderedFields = getOrderedFields();

  // Render field item
  const [editingBaseField, setEditingBaseField] = useState(null);

  const renderFieldItem = (field) => {
    const isBase = field.isBase;
    const isDragging = draggedFieldId === field.id;
    const isDragOver = dragOverFieldId === field.id;
    const isEditingThis = editingBaseField === field.id;
    
    return (
      <div
        key={field.id}
        draggable={!isEditingThis}
        onDragStart={(e) => handleDragStart(e, field.id)}
        onDragOver={(e) => handleDragOver(e, field.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, field.id)}
        onDragEnd={handleDragEnd}
        className={`
          flex flex-col p-3 rounded-lg border cursor-move transition-all
          ${isBase ? 'bg-slate-50 border-slate-200' : 'bg-blue-50 border-blue-200'}
          ${isDragging ? 'opacity-50 scale-95' : ''}
          ${isDragOver ? 'border-indigo-500 border-2 bg-indigo-50' : ''}
          ${!isBase && !field.isActive ? 'opacity-60' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700 truncate">{field.customLabel || field.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${isBase ? 'bg-slate-200 text-slate-600' : 'bg-blue-100 text-blue-600'}`}>
                  {field.type}
                </span>
                {field.required && <span className="text-xs text-orange-600">Req</span>}
                {isBase && field.customLabel && field.customLabel !== field.label && (
                  <span className="text-[10px] text-slate-400">(base: {field.label})</span>
                )}
              </div>
              <span className="text-xs text-slate-400">ID: {field.id}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isBase ? (
              <>
                <button
                  onClick={() => setEditingBaseField(isEditingThis ? null : field.id)}
                  className="p-1.5 rounded hover:bg-slate-200 text-slate-500 transition-colors"
                  title="Editar label / placeholder"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => toggleBaseFieldPublic(field.id)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                    field.showInPublicForm 
                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                      : 'bg-slate-200 text-slate-500 hover:bg-slate-300'
                  }`}
                  title={field.showInPublicForm ? 'Visible en formulario público' : 'Oculto en formulario público'}
                >
                  {field.showInPublicForm ? (
                    <><CheckCircle className="w-3.5 h-3.5" /><span>Público</span></>
                  ) : (
                    <span>Oculto</span>
                  )}
                </button>
              </>
            ) : (
            // Custom field: toggle active/inactive and edit
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleEditField(field)}
                className="p-1.5 rounded hover:bg-blue-200 text-blue-600 transition-colors"
                title="Editar campo"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => toggleFieldActive(field.id)}
                className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                  field.isActive 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                }`}
                title={field.isActive ? 'Campo activo' : 'Campo inactivo'}
              >
                {field.isActive ? (
                  <ToggleRight className="w-5 h-5" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
                <span className="text-xs font-medium">{field.isActive ? 'Activo' : 'Inactivo'}</span>
              </button>
            </div>
          )}
        </div>
        </div>
        
        {/* Inline edit for base field label/placeholder */}
        {isBase && isEditingThis && (
          <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Label personalizado</label>
              <input type="text" value={field.customLabel || field.label}
                onChange={(e) => setBaseFields(baseFields.map(f => f.id === field.id ? { ...f, customLabel: e.target.value } : f))}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg"
                placeholder={field.label} />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Placeholder personalizado</label>
              <input type="text" value={field.customPlaceholder || field.placeholder || ''}
                onChange={(e) => setBaseFields(baseFields.map(f => f.id === field.id ? { ...f, customPlaceholder: e.target.value } : f))}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg"
                placeholder={`Ej: Ingresa tu ${field.label.toLowerCase()}`} />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Load WhatsApp config when pipeline changes
  useEffect(() => {
    if (selectedPipeline) {
      const loadWhatsAppConfig = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/whatsapp-widget/${selectedPipeline}`);
          if (response.ok) {
            const data = await response.json();
            if (data.whatsappConfig) {
              setWhatsappConfig(data.whatsappConfig);
            }
          }
        } catch (err) {
          console.log('Error loading WhatsApp config:', err);
        }
      };
      loadWhatsAppConfig();
    }
  }, [selectedPipeline, BACKEND_URL]);

  // Save WhatsApp configuration
  const handleSaveWhatsappConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/pipelines/${selectedPipeline}/whatsapp-widget`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(whatsappConfig)
      });
      if (response.ok) {
        toast.success('Configuración de WhatsApp guardada');
      } else {
        toast.error('Error al guardar la configuración');
      }
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  // Generate WhatsApp widget embed code - uses current domain for production compatibility
  const generateWhatsappEmbedCode = () => {
    const currentDomain = window.location.origin;
    return `<!-- Widget de WhatsApp - DataCRM -->
<script src="${currentDomain}/api/whatsapp-widget/${selectedPipeline}/script.js" async></script>`;
  };

  // Copy WhatsApp embed code
  const handleCopyWhatsappCode = () => {
    navigator.clipboard.writeText(generateWhatsappEmbedCode());
    setWhatsappCopied(true);
    toast.success('Código del widget copiado');
    setTimeout(() => setWhatsappCopied(false), 2000);
  };

  // Add field to WhatsApp form
  const addWhatsappField = () => {
    // WhatsApp only allows adding non-base fields (custom fields)
    // The 3 base fields (name, email, phone) are always required
    setWhatsappConfig({
      ...whatsappConfig,
      fields: [...whatsappConfig.fields, { name: `field_${Date.now()}`, label: 'Nuevo Campo', required: false, type: 'text' }]
    });
  };

  // Remove field from WhatsApp form - only allows removing non-base fields
  const removeWhatsappField = (index) => {
    const field = whatsappConfig.fields[index];
    // Prevent removing base fields (name, email, phone)
    if (field.name === 'name' || field.name === 'email' || field.name === 'phone') {
      return; // Cannot remove base fields
    }
    setWhatsappConfig({
      ...whatsappConfig,
      fields: whatsappConfig.fields.filter((_, i) => i !== index)
    });
  };

  // Update WhatsApp field
  const updateWhatsappField = (index, updates) => {
    setWhatsappConfig({
      ...whatsappConfig,
      fields: whatsappConfig.fields.map((f, i) => i === index ? { ...f, ...updates } : f)
    });
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Constructor de Formularios</h1>
        <p className="text-slate-500 mt-2">Configura los campos del formulario de captura para cada embudo</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('form')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'form'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Code className="w-4 h-4" />
          Formulario Web
        </button>
        <button
          onClick={() => setActiveTab('whatsapp')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'whatsapp'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          Widget WhatsApp
        </button>
      </div>

      {/* WhatsApp Widget Tab */}
      {activeTab === 'whatsapp' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Pipeline Selection for WhatsApp */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-500" />
                Configuración del Widget WhatsApp
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Embudo</label>
                    <select
                      value={selectedPipeline}
                      onChange={(e) => setSelectedPipeline(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    >
                      {state.pipelines.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-slate-700">Widget Activo</label>
                    <button
                      onClick={() => setWhatsappConfig({ ...whatsappConfig, enabled: !whatsappConfig.enabled })}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        whatsappConfig.enabled ? 'bg-green-500' : 'bg-slate-300'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        whatsappConfig.enabled ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Número de WhatsApp
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={whatsappConfig.phoneCountry || '+57'}
                        onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phoneCountry: e.target.value })}
                        className="w-24 px-2 py-2 border border-slate-200 rounded-lg text-sm"
                      >
                        {COUNTRY_CODES.map(c => (
                          <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        value={whatsappConfig.phoneNumber}
                        onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phoneNumber: e.target.value })}
                        placeholder="300 123 4567"
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Número completo con código de país</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Posición en pantalla</label>
                    <select
                      value={whatsappConfig.position}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, position: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    >
                      <option value="bottom-right">Inferior Derecha</option>
                      <option value="bottom-left">Inferior Izquierda</option>
                      <option value="top-right">Superior Derecha</option>
                      <option value="top-left">Superior Izquierda</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Color del botón</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={whatsappConfig.buttonColor}
                        onChange={(e) => setWhatsappConfig({ ...whatsappConfig, buttonColor: e.target.value })}
                        className="w-12 h-10 border border-slate-200 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={whatsappConfig.buttonColor}
                        onChange={(e) => setWhatsappConfig({ ...whatsappConfig, buttonColor: e.target.value })}
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Título del popup</label>
                    <input
                      type="text"
                      value={whatsappConfig.headerText}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, headerText: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="¡Contáctanos por WhatsApp!"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mensaje inicial</label>
                  <textarea
                    value={whatsappConfig.message}
                    onChange={(e) => setWhatsappConfig({ ...whatsappConfig, message: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none"
                    placeholder="Hola, me gustaría más información"
                  />
                  <p className="text-xs text-slate-500 mt-1">Este mensaje aparecerá al inicio en WhatsApp</p>
                </div>
              </div>
            </div>

            {/* WhatsApp Form Fields */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Campos del Formulario</h3>
                  <p className="text-sm text-slate-500">Campos que el visitante completará antes de ir a WhatsApp</p>
                </div>
                <button
                  onClick={addWhatsappField}
                  className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600"
                >
                  <Plus className="w-4 h-4" />
                  Agregar Campo
                </button>
              </div>

              <div className="space-y-3">
                {whatsappConfig.fields.map((field, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateWhatsappField(index, { label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                        placeholder="Nombre del campo"
                        className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                      />
                      
                      <select
                        value={field.type}
                        onChange={(e) => updateWhatsappField(index, { type: e.target.value })}
                        className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                      >
                        <option value="text">Texto</option>
                        <option value="email">Email</option>
                        <option value="tel">Teléfono</option>
                        <option value="number">Número</option>
                        <option value="textarea">Texto largo</option>
                      </select>

                      <input
                        type="text"
                        value={field.placeholder || ''}
                        onChange={(e) => updateWhatsappField(index, { placeholder: e.target.value })}
                        placeholder="Placeholder..."
                        className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                      />

                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-sm text-slate-600">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateWhatsappField(index, { required: e.target.checked })}
                            className="rounded"
                          />
                          Req.
                        </label>
                        <button
                          onClick={() => removeWhatsappField(index)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded"
                          title="Eliminar campo"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {whatsappConfig.fields.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No hay campos configurados</p>
                    <p className="text-sm">Agrega campos para capturar datos antes de redirigir a WhatsApp</p>
                  </div>
                )}
              </div>
              
              {/* Privacy Policy for WhatsApp */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-md font-semibold text-slate-900">Política de Privacidad</h4>
                    <p className="text-sm text-slate-500">Checkbox obligatorio antes de enviar</p>
                  </div>
                  <button
                    onClick={() => setWhatsappConfig({ ...whatsappConfig, privacyPolicyEnabled: !whatsappConfig.privacyPolicyEnabled })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      whatsappConfig.privacyPolicyEnabled ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      whatsappConfig.privacyPolicyEnabled ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {whatsappConfig.privacyPolicyEnabled && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">URL de Políticas</label>
                      <input
                        type="url"
                        value={whatsappConfig.privacyPolicyUrl || ''}
                        onChange={(e) => setWhatsappConfig({ ...whatsappConfig, privacyPolicyUrl: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="https://tusitio.com/politicas"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Texto del Checkbox</label>
                      <input
                        type="text"
                        value={whatsappConfig.privacyPolicyText || 'He leído y acepto la Política de Privacidad'}
                        onChange={(e) => setWhatsappConfig({ ...whatsappConfig, privacyPolicyText: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="He leído y acepto la Política de Privacidad"
                      />
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleSaveWhatsappConfig}
                disabled={saving}
                className="mt-4 w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </div>
          </div>

          {/* WhatsApp Preview & Code Panel */}
          <div className="space-y-6">
            {/* Embed Code */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Code className="w-4 h-4" />
                Código para tu sitio web
              </h3>
              <div className="bg-slate-900 rounded-lg p-3 mb-3">
                <pre className="text-green-400 text-xs whitespace-pre-wrap break-all">
                  {generateWhatsappEmbedCode()}
                </pre>
              </div>
              <button
                onClick={handleCopyWhatsappCode}
                className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                  whatsappCopied
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {whatsappCopied ? '✓ Copiado' : 'Copiar Código'}
              </button>
            </div>

            {/* Widget Preview */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-100 border-b flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Vista Previa</span>
                <button
                  onClick={() => setShowWhatsappPreview(!showWhatsappPreview)}
                  className="text-xs text-indigo-600 hover:underline"
                >
                  {showWhatsappPreview ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              
              {showWhatsappPreview && (
                <div className="p-4 bg-slate-50 min-h-[400px] relative">
                  {/* Mock preview */}
                  <div className={`absolute ${whatsappConfig.position.includes('bottom') ? 'bottom-4' : 'top-4'} ${whatsappConfig.position.includes('right') ? 'right-4' : 'left-4'}`}>
                    <div className="w-[280px] bg-white rounded-2xl shadow-xl overflow-hidden mb-2 border border-slate-200">
                      <div className="p-4" style={{ background: `linear-gradient(135deg, ${whatsappConfig.buttonColor}, #128C7E)` }}>
                        <h4 className="text-white font-semibold text-sm">{whatsappConfig.headerText}</h4>
                      </div>
                      <div className="p-4 space-y-3">
                        {whatsappConfig.fields.map((field, idx) => (
                          <div key={idx}>
                            <label className="block text-xs text-slate-600 mb-1">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type={field.type}
                              placeholder={field.placeholder || field.label}
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
                              disabled
                            />
                          </div>
                        ))}
                        {whatsappConfig.privacyPolicyEnabled && (
                          <div className="flex items-start gap-2 my-3">
                            <input type="checkbox" className="mt-1" disabled />
                            <label className="text-xs text-slate-600">
                              {whatsappConfig.privacyPolicyText || 'He leído y acepto la Política de Privacidad'}*
                            </label>
                          </div>
                        )}
                        <button
                          className="w-full py-2.5 rounded-lg text-white text-sm font-medium flex items-center justify-center gap-2"
                          style={{ backgroundColor: whatsappConfig.buttonColor }}
                          disabled
                        >
                          <MessageCircle className="w-4 h-4" />
                          Enviar a WhatsApp
                        </button>
                        {whatsappConfig.privacyPolicyEnabled && whatsappConfig.privacyPolicyUrl && (
                          <p className="text-center mt-2 text-xs" style={{ color: whatsappConfig.buttonColor }}>
                            *Leer Políticas de Privacidad Aquí
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center ml-auto"
                      style={{ backgroundColor: whatsappConfig.buttonColor }}
                      disabled
                    >
                      <MessageCircle className="w-7 h-7 text-white" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Status Info */}
            <div className={`rounded-xl border p-4 ${whatsappConfig.enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {whatsappConfig.enabled ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <MessageCircle className="w-5 h-5 text-amber-600" />
                )}
                <span className={`font-medium ${whatsappConfig.enabled ? 'text-green-700' : 'text-amber-700'}`}>
                  {whatsappConfig.enabled ? 'Widget Activo' : 'Widget Inactivo'}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                {whatsappConfig.enabled
                  ? 'El widget se mostrará en tu sitio web cuando insertes el código.'
                  : 'Activa el widget y guarda la configuración para usarlo en tu sitio.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Builder Tab */}
      {activeTab === 'form' && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Configuration Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pipeline Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Embudo</label>
                <select
                  value={selectedPipeline}
                  onChange={(e) => setSelectedPipeline(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {state.pipelines.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título del Formulario</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="¡Hagamos que tus ideas despeguen!"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subtítulo (opcional)</label>
                <input
                  type="text"
                  value={formSubtitle}
                  onChange={(e) => setFormSubtitle(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Contáctanos y hablemos para hacerlas realidad."
                />
                <p className="text-xs text-slate-500 mt-1">Aparece debajo del título con una línea decorativa</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Texto del Botón</label>
                <input
                  type="text"
                  value={submitText}
                  onChange={(e) => setSubmitText(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tamaño del Formulario</label>
                <select
                  value={formSize}
                  onChange={(e) => setFormSize(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="compact">Compacto (espacios reducidos)</option>
                  <option value="normal">Normal</option>
                  <option value="large">Grande (más espaciado)</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">Ajusta el espaciado y padding del formulario</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Código de País (Teléfono)</label>
                <select
                  value={selectedCountryCode}
                  onChange={(e) => setSelectedCountryCode(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {COUNTRY_CODES.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code} - {country.country}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Código predeterminado para el campo de teléfono</p>
              </div>
            </div>
            
            {/* Personalización de Estilos */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-md font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5" style={{ color: primaryColor }} />
                Personalización de Estilos
              </h3>
              
              {/* Background */}
              <div className="mb-4 pb-4 border-b border-slate-100">
                <label className="block text-xs font-semibold text-slate-700 mb-2">Color de Fondo</label>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border border-slate-200 flex-shrink-0" style={{ backgroundColor: formStyle.backgroundColor }} />
                  <input type="text" value={formStyle.backgroundColor}
                    onChange={(e) => setFormStyle({ ...formStyle, backgroundColor: e.target.value })}
                    className="flex-1 px-3 py-1.5 text-sm font-mono border border-slate-200 rounded-lg"
                    placeholder="rgba(0,0,0,0.85) o #hex" />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Usa rgba() para opacidad. Ej: rgba(0,0,0,0.85)</p>
              </div>

              {/* Color Grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {[
                  { key: 'labelColor', label: 'Color de Labels' },
                  { key: 'inputBorderColor', label: 'Borde Inputs', fallback: '#d1d5db' },
                  { key: 'inputBgColor', label: 'Fondo Inputs' },
                  { key: 'inputTextColor', label: 'Texto Inputs' },
                  { key: 'buttonColor', label: 'Color Botón' },
                  { key: 'buttonTextColor', label: 'Texto Botón' },
                ].map(({ key, label, fallback }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                    <div className="flex items-center gap-2">
                      <label className="relative w-8 h-8 rounded-lg border-2 border-slate-200 cursor-pointer overflow-hidden flex-shrink-0 hover:border-slate-400 transition-colors">
                        <input type="color" value={formStyle[key] || fallback || '#000000'}
                          onChange={(e) => setFormStyle({ ...formStyle, [key]: e.target.value })}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                        <div className="w-full h-full rounded-md flex items-center justify-center" style={{ backgroundColor: formStyle[key] || fallback || '#000000' }}>
                          <Palette className="w-3.5 h-3.5" style={{ color: (formStyle[key] || fallback || '#000000') < '#888888' ? '#ffffff' : '#000000', opacity: 0.5 }} />
                        </div>
                      </label>
                      <input type="text" value={formStyle[key] || fallback || ''}
                        onChange={(e) => setFormStyle({ ...formStyle, [key]: e.target.value })}
                        className="flex-1 px-2.5 py-1.5 text-xs font-mono border border-slate-200 rounded-lg"
                        placeholder="#hex" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Opacity + Font */}
              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Opacidad Botón: {formStyle.buttonOpacity || 100}%</label>
                  <input type="range" min="10" max="100" value={formStyle.buttonOpacity || 100}
                    onChange={(e) => setFormStyle({ ...formStyle, buttonOpacity: parseInt(e.target.value) })}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer" />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                    <span>Transparente</span><span>Sólido</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Fuente</label>
                  <select value={formStyle.fontFamily}
                    onChange={(e) => setFormStyle({ ...formStyle, fontFamily: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
                    <option value="system-ui, -apple-system, sans-serif">Sistema (Default)</option>
                    <option value="'Inter', sans-serif">Inter</option>
                    <option value="'Roboto', sans-serif">Roboto</option>
                    <option value="'Open Sans', sans-serif">Open Sans</option>
                    <option value="'Montserrat', sans-serif">Montserrat</option>
                    <option value="'Poppins', sans-serif">Poppins</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Georgia, serif">Georgia</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Privacy Policy Configuration */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-md font-semibold text-slate-900">Política de Privacidad</h3>
                  <p className="text-sm text-slate-500">Checkbox de aceptación de políticas</p>
                </div>
                <button
                  onClick={() => setPrivacyPolicyConfig({ ...privacyPolicyConfig, enabled: !privacyPolicyConfig.enabled })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    privacyPolicyConfig.enabled ? 'bg-green-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    privacyPolicyConfig.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {privacyPolicyConfig.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">URL de Política de Privacidad</label>
                    <input
                      type="url"
                      value={privacyPolicyConfig.url}
                      onChange={(e) => setPrivacyPolicyConfig({ ...privacyPolicyConfig, url: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="https://tusitio.com/politicas-privacidad"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Texto del Checkbox</label>
                    <input
                      type="text"
                      value={privacyPolicyConfig.text}
                      onChange={(e) => setPrivacyPolicyConfig({ ...privacyPolicyConfig, text: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="He leído y acepto la Política de Privacidad"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Texto del Enlace (debajo del botón)</label>
                    <input
                      type="text"
                      value={privacyPolicyConfig.linkText}
                      onChange={(e) => setPrivacyPolicyConfig({ ...privacyPolicyConfig, linkText: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="*Leer Políticas de Privacidad Aquí"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fields Configuration */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Campos del Formulario</h2>
                <p className="text-sm text-slate-500">Arrastra para reordenar • Los campos base usan ✓ para formularios públicos</p>
              </div>
              <button
                onClick={() => setShowAddField(true)}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="w-4 h-4" />
                Agregar Campo
              </button>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mb-4 p-3 bg-slate-50 rounded-lg text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-slate-200 rounded"></div>
                <span className="text-slate-600">Campo Base (obligatorio interno)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-200 rounded"></div>
                <span className="text-slate-600">Campo Personalizado</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                <span className="text-slate-600">Visible en formulario público</span>
              </div>
            </div>

            {/* Fields List */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {orderedFields.map(field => renderFieldItem(field))}
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveConfiguration}
              disabled={saving}
              className="mt-4 w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Guardando...' : 'Guardar Configuración del Embudo'}
            </button>
          </div>

          {/* Add Field Modal */}
          {showAddField && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md m-4 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Agregar Campo</h3>
                
                {/* Hidden base fields that can be enabled */}
                {(() => {
                  const hiddenBase = baseFields.filter(f => !f.showInPublicForm);
                  if (hiddenBase.length === 0) return null;
                  return (
                    <div className="mb-5">
                      <p className="text-sm font-medium text-slate-700 mb-2">Campos base disponibles</p>
                      <div className="space-y-1.5">
                        {hiddenBase.map(f => (
                          <button key={f.id} onClick={() => {
                            setBaseFields(baseFields.map(bf => bf.id === f.id ? { ...bf, showInPublicForm: true } : bf));
                            toast.success(`Campo "${f.label}" agregado al formulario`);
                          }}
                            className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors text-left">
                            <span className="text-sm text-slate-700">{f.label}</span>
                            <span className="text-xs text-green-600 font-medium">+ Agregar</span>
                          </button>
                        ))}
                      </div>
                      <div className="border-t border-slate-200 mt-4 pt-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">O crear campo personalizado</p>
                      </div>
                    </div>
                  );
                })()}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del Campo</label>
                    <input
                      type="text"
                      value={newField.label}
                      onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="Ej: Presupuesto, Fecha de inicio..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de Campo</label>
                    <select
                      value={newField.type}
                      onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    >
                      {FIELD_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  {(newField.type === 'select' || newField.type === 'radio') && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Opciones (separadas por coma)</label>
                      <input
                        type="text"
                        value={newField.options}
                        onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        placeholder="Opción 1, Opción 2, Opción 3"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Placeholder</label>
                    <input
                      type="text"
                      value={newField.placeholder}
                      onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="Texto de ayuda..."
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="required"
                      checked={newField.required}
                      onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="required" className="text-sm text-slate-700">Campo requerido</label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowAddField(false)}
                    className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddField}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Agregar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Field Modal */}
          {showEditField && editingField && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Editar Campo</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del campo</label>
                    <input
                      type="text"
                      value={editingField.label}
                      onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de campo</label>
                    <select
                      value={editingField.type}
                      onChange={(e) => setEditingField({ ...editingField, type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="text">Texto</option>
                      <option value="email">Email</option>
                      <option value="phone">Teléfono</option>
                      <option value="number">Número</option>
                      <option value="textarea">Área de texto</option>
                      <option value="select">Lista desplegable</option>
                      <option value="radio">Opciones</option>
                      <option value="date">Fecha</option>
                      <option value="checkbox">Casilla de verificación</option>
                    </select>
                  </div>

                  {(editingField.type === 'select' || editingField.type === 'radio') && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Opciones (separadas por comas)
                      </label>
                      <input
                        type="text"
                        value={editingField.options}
                        onChange={(e) => setEditingField({ ...editingField, options: e.target.value })}
                        placeholder="Opción 1, Opción 2, Opción 3"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Placeholder</label>
                    <input
                      type="text"
                      value={editingField.placeholder || ''}
                      onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                      placeholder="Texto de ayuda..."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-required"
                      checked={editingField.required}
                      onChange={(e) => setEditingField({ ...editingField, required: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded"
                    />
                    <label htmlFor="edit-required" className="text-sm text-slate-700">Campo requerido</label>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditField(false);
                      setEditingField(null);
                    }}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveEditField}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview & Code Panel */}
        <div className="space-y-6">
          {/* Action Buttons */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showPreview ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <Eye className="w-4 h-4" />
                Vista Previa
              </button>
              <button
                onClick={handleCopyCode}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
              >
                {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copiado' : 'Copiar HTML'}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Resumen de Campos</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-slate-900">{baseFields.filter(f => f.showInPublicForm).length}</div>
                <div className="text-slate-500">Base públicos</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{customFields.filter(f => f.isActive).length}</div>
                <div className="text-slate-500">Personalizados activos</div>
              </div>
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <span className="text-xs text-slate-500 ml-2">Vista Previa (solo campos públicos/activos)</span>
              </div>
              <iframe
                srcDoc={generateFormCode()}
                title="Form Preview"
                className="w-full h-[600px] border-0"
              />
            </div>
          )}

          {/* Code Preview */}
          {!showPreview && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-800 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Code className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-400">Código para Incrustar</span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={embedCodeType}
                    onChange={(e) => setEmbedCodeType(e.target.value)}
                    className="text-xs bg-slate-700 text-slate-200 border border-slate-600 rounded px-2 py-1"
                  >
                    <option value="compact">Compacto (recomendado)</option>
                    <option value="full">Completo (HTML independiente)</option>
                  </select>
                </div>
              </div>
              {embedCodeType === 'compact' && (
                <div className="px-4 py-2 bg-emerald-900/30 border-b border-slate-700">
                  <p className="text-xs text-emerald-400">
                    ✓ Script ligero (~3KB) • No afecta estilos de tu página • Fácil de integrar
                  </p>
                </div>
              )}
              <pre className="p-4 bg-slate-900 text-green-400 text-xs overflow-auto h-[500px]">
                {embedCodeType === 'compact' ? generateCompactEmbedCode() : generateFormCode()}
              </pre>
            </div>
          )}
        </div>
      </div>

      </>
      )}
    </div>
  );
};

export default FormBuilder;
